'use client';

/**
 * Foundry Markdown Renderer
 * Renders markdown content with brand styling for business plan sections
 */

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

// Custom components for brand-consistent styling
const components: Components = {
  h1: ({ children }) => (
    <h1 className="text-2xl font-bold text-foreground mb-4 mt-6 first:mt-0">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-xl font-semibold text-foreground mb-3 mt-5 first:mt-0">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-lg font-semibold text-foreground mb-2 mt-4 first:mt-0">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-base font-semibold text-foreground mb-2 mt-3 first:mt-0">{children}</h4>
  ),
  p: ({ children }) => (
    <p className="text-foreground-secondary mb-3 leading-relaxed">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="list-disc list-inside mb-3 space-y-1 text-foreground-secondary ml-2">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal list-inside mb-3 space-y-1 text-foreground-secondary ml-2">{children}</ol>
  ),
  li: ({ children }) => (
    <li className="text-foreground-secondary">{children}</li>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-foreground">{children}</strong>
  ),
  em: ({ children }) => (
    <em className="italic text-foreground/90">{children}</em>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-border-muted0 pl-4 my-4 italic text-foreground-secondary">
      {children}
    </blockquote>
  ),
  code: ({ children, className }) => {
    // Inline code vs code block
    const isBlock = className?.includes('language-');
    if (isBlock) {
      return (
        <code className="block bg-background/80 rounded-lg p-4 text-sm font-mono text-foreground/90 overflow-x-auto my-3">
          {children}
        </code>
      );
    }
    return (
      <code className="bg-background/60 rounded px-1.5 py-0.5 text-sm font-mono text-accent">
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="bg-background/80 rounded-lg p-4 overflow-x-auto my-3">
      {children}
    </pre>
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      className="text-accent hover:text-accent underline underline-offset-2 transition-colors"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto my-4">
      <table className="w-full border-collapse text-sm">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-background/60">{children}</thead>
  ),
  tbody: ({ children }) => (
    <tbody className="divide-y divide-border-muted">{children}</tbody>
  ),
  tr: ({ children }) => (
    <tr className="hover:bg-background/40">{children}</tr>
  ),
  th: ({ children }) => (
    <th className="px-3 py-2 text-left text-foreground font-semibold border-b border-border">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-3 py-2 text-foreground-secondary border-b border-border-muted">
      {children}
    </td>
  ),
  hr: () => (
    <hr className="border-border my-6" />
  ),
};

export function MarkdownRenderer({ content, className = '' }: MarkdownRendererProps) {
  return (
    <div className={`prose prose-invert prose-sm max-w-none ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
